if (!customElements.get('video-hero-banner')) {
  class VideoHeroBanner extends HTMLElement {

    constructor() {
      super();
      this.vimeoPlayers = [];
      this.youtubePlayers = [];
      this.mq = window.matchMedia('(max-width: 549px)');
      this.observer = new IntersectionObserver(this.loadVideos.bind(this), { threshold: 0 });
      this.toggleOnResize = this.getAttribute('data-toggle-on-resize');
      this.lazyload = this.getAttribute('data-lazyload');
      this.controls = this.getAttribute('data-controls');
      this.autoplay = this.getAttribute('data-autoplay');
      this.autoplayControls = this.getAttribute('data-autoplay-controls');
      this.videos = this.querySelectorAll('.js-hero-banner-video');
      this.externalVideos = this.querySelectorAll('.js-hero-banner-external-video');
      this.desktopVideos = this.querySelectorAll('.js-hero-banner-video-desktop');
      this.mobileVideos = this.querySelectorAll('.js-hero-banner-video-mobile');
      this.youtubeVideos = this.querySelectorAll('.js-hero-banner-video-youtube');
      this.hasYoutubeVideos = this.youtubeVideos.length > 0;
      this.init();
    }

    init() {
      const loadVimeoScript = this.getAttribute('data-load-vimeo-script');
      const loadYoutubeScript = this.getAttribute('data-load-youtube-script');

      if (!loadVimeoScript && !loadYoutubeScript) this.listeners();

      this.youtubeVideos.forEach((video) => {
        this.youtubePlayers.push({
          id: video.id,
          status: 'waiting'
        })
      });

      const writeScriptTag = (src, callback) => {
        const script = document.createElement('script');
        const scriptTags = document.getElementsByTagName('script');
  
        script.onload = callback;
        script.src = src;
        scriptTags[0].parentNode.insertBefore(script, scriptTags[0]);
      }
      
      const status = [];
      const updateReadyStatus = () => {
        if(status.every(script => script === 'ready')) this.listeners();
      }

      if (loadVimeoScript) {
        const index = status.push('waiting') - 1;

        writeScriptTag('https://player.vimeo.com/api/player.js', () => {
          status[index] = ('ready');
          updateReadyStatus();
        });
      }

      if (loadYoutubeScript) {
        const index = status.push('waiting') - 1;

        writeScriptTag('https://www.youtube.com/iframe_api', () => {
          YT.ready(() => {
            status[index] = ('ready');
            updateReadyStatus();
          })
        });
      }
    }

    listeners() {
      if (this.lazyload) {
        this.observer.observe(this);
      } else {
        this.externalVideos.forEach((video) => {
          const type = this.getElementAttribute(video, 'data-video-type');

          if (type === 'vimeo') {
            this.initVimeo(video);
          } else if (type === 'youtube') {
            this.initYoutube(video);
          }
        });

        if (!this.hasYoutubeVideos) {
          this.resizeChecker(this.mq);
          this.mq.addListener(this.resizeChecker.bind(this));
        }
      }
    }

    checkYoutubeStatus() {
      if (this.youtubePlayers.every(player => player.status === 'ready')) {       
        this.resizeChecker(this.mq);
        this.mq.addListener(this.resizeChecker.bind(this));
      }
    }

    getElementAttribute(element, attribute) {
      if (!element || !attribute) return;
      return element.getAttribute(attribute) ? element.getAttribute(attribute) : '';
    }

    playVideo(player, type) {
      if (!player) return;
      
      if (type === 'youtube') {
        player.playVideo(); 
      } else {
        player.play(); 
      }
    }

    pauseVideo(player, type) {
      if (!player) return;

      if (type === 'youtube') {
        player.pauseVideo(); 
      } else {
        player.pause(); 
      }
    }
    
    muteVideo(player, type) {
      if (!player) return;

      if (type === 'video') {
        player.muted = true;
      }

      if (type === 'youtube') {
        player.mute();
      }
    }

    loadVideos(entries) {
      if (!this.lazyload) return;

      let intersecting = false;

      entries.forEach((entry) => {
        if (entry.target === this && entry.isIntersecting) {
          intersecting = true
        }
      });

      if (!intersecting) return;
      
      this.videos.forEach((video) => {
        const type = this.getElementAttribute(video, 'data-video-type');

        if (type === 'vimeo') {
          this.initVimeo(video);
        } else if (type === 'youtube') {
          this.initYoutube(video);
        } else {
          const sources = video.getAttribute('data-sources').split(':');

          sources.forEach((source) => {
            const parts = source.split('|');
            const url = parts[0];
            const type = parts[1];
  
            if (!url || !type) return;
  
            const sourceTag = `<source src="${url}" type="${type}">`;
            video.innerHTML += sourceTag;
          })
        }
      })

      this.resizeChecker(this.mq);
      this.mq.addListener(this.resizeChecker.bind(this));
      this.observer.disconnect();
    }

    resizeChecker(mq) {
      const toggleVideo = (video, breakpoint) => {
        let player = video;
        const type = this.getElementAttribute(video, 'data-video-type');

        if (type === 'vimeo') {
          const targetVideo = this.vimeoPlayers.find(vimeoVideo => vimeoVideo.id === video.id)
          player = targetVideo.player;
        }

        if (type === 'youtube') {
          const targetVideo = this.youtubePlayers.find(youtubeVideo => youtubeVideo.id === video.id)
          player = targetVideo.player;
        }

        const switchVideo = () => {
          if (!this.controls || this.autoplayControls) {
            if (type === 'vimeo') {
              player.setVolume(0)
              .then(() => {
                this.playVideo(player, type);
              })
            } else {
              this.muteVideo(player, type);
              this.playVideo(player, type);
            }
          }
        }

        if (breakpoint === 'desktop') {
          if (mq.matches) {
            this.pauseVideo(player, type);
          } else {
            switchVideo();
          }
        }

        if (breakpoint === 'mobile') {
          if (mq.matches) {
            switchVideo();
          } else {
            this.pauseVideo(player, type);
          }
        }
      }

      this.desktopVideos.forEach((video) => {
        toggleVideo(video, 'desktop');
      })

      this.mobileVideos.forEach((video) => {
        toggleVideo(video, 'mobile');
      })
    }

    initVimeo(video) {
      let options = {
        autoplay: false,
        url: this.getElementAttribute(video, 'data-source')
      };

      let attributes = {
        controls: false,
        background: true,
        loop: true,
        muted: true
      }

      if (this.autoplayControls) {
        attributes = {
          controls: true,
          muted: true
        }
      } else {
        if (this.autoplay) {
          attributes = {
            controls: false,
            loop: true,
            background: true,
            muted: true
          }
        }

        if (this.controls) {
          attributes = {
            controls: true,
            muted: false
          }
        }
      }

      options = { ...options, ...attributes };

      const player = new Vimeo.Player(video, options);

      this.vimeoPlayers.push({
        id: video.id,
        player: player
      });

      if (this.toggleOnResize) return;

      if (this.autoplay || this.autoplayControls) {
        this.playVideo(player, 'vimeo');
      }
    }

    initYoutube(video) {      
      const videoId = this.getElementAttribute(video, 'data-source');

      let options = {
        autoplay: 0,
        disablekb: 1,
        enablejsapi: 1,
        modestbranding: 1,
        playlist: videoId,
        playsinline: 1,
      }

      let attributes = {
        controls: 0,
        loop: 1
      } 

      if (this.autoplayControls) {
        attributes = {
          controls: 1
        }
      } else {
        if (this.autoplay) {
          attributes = {
            controls: 0,
            loop: 1
          }
        }

        if (this.controls) {
          attributes = {
            controls: 1
          }
        }
      }

      options = { ...options, ...attributes };

      const onPlayerReady = (event) => {
        const player = event.target;
        const playerObject = this.youtubePlayers.find((player) => player.id === video.id);

        if (!playerObject) return;

        playerObject.player = player;
        playerObject.status = 'ready';

        if (this.toggleOnResize) {
          this.checkYoutubeStatus();
          return;
        }

        if (this.autoplay || this.autoplayControls) {
          this.muteVideo(playerObject.player, 'youtube');
          this.playVideo(playerObject.player, 'youtube');
        }
      }

      const player = new YT.Player(video.id, {
        videoId: videoId,
        playerVars: {
          ...options
        },
        events: {
          'onReady': onPlayerReady
        }
      });
    }
  }

  customElements.define('video-hero-banner', VideoHeroBanner);
}
